import React, { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import { OnboardingDraft, ValidationIssue } from '@intelligent-play-v2/domain';

import { Button, ButtonType } from '~/components/button';
import { TextInput } from '~/components/forms';

import { PerspectiveTransformField } from './PerspectiveTransformField';
import { fieldStateFor, inputStateClass, messageAt } from './fieldState';
import { useDraftField } from './useDraftField';

interface OnboardingSensorRowProps {
  pitchIndex: number;
  sensorIndex: number;
  issues: ValidationIssue[];
  onRemove: () => void;
  canRemove: boolean;
}

/**
 * One sensor: id, lens spec, perspective transform.
 *
 * All three are required, and each field says so itself — red until it is filled in with
 * something valid. This replaced a "lens spec verified" tickbox: a filled-in lens spec
 * already means a human looked at the sensor, so asking for a tick as well was asking the
 * same question twice.
 */
export const OnboardingSensorRow: FC<OnboardingSensorRowProps> = ({
  pitchIndex,
  sensorIndex,
  issues,
  onRemove,
  canRemove,
}) => {
  const { register } = useFormContext<OnboardingDraft>();
  const base = `pitches.${pitchIndex}.sensors.${sensorIndex}` as const;
  const [perspectiveTransform, setPerspectiveTransform] = useDraftField(
    `${base}.perspectiveTransform` as const
  );

  return (
    // the fields inside say what is missing; colouring the whole row as well is just noise
    <div className="rounded border border-primary-250 bg-white p-4">
      <div className="flex items-center justify-between">
        <p className="text-base font-semibold">Sensor {sensorIndex + 1}</p>
        {canRemove && (
          <Button
            text="Remove sensor"
            size="xsmall"
            type={ButtonType.OutlineDanger}
            onClick={onRemove}
          />
        )}
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <TextInput
          label="Sensor ID"
          isLightMode
          required
          type="number"
          register={register(`${base}.id` as const, {
            setValueAs: value => (value === '' ? null : Number(value)),
          })}
          className={inputStateClass(fieldStateFor(issues, `${base}.id`))}
          errorMessage={messageAt(issues, `${base}.id`)}
        />
        <TextInput
          label="Lens spec (mm)"
          isLightMode
          required
          type="number"
          register={register(`${base}.lensSpecMillimeters` as const, {
            setValueAs: value => (value === '' ? null : Number(value)),
          })}
          className={inputStateClass(
            fieldStateFor(issues, `${base}.lensSpecMillimeters`)
          )}
          errorMessage={messageAt(issues, `${base}.lensSpecMillimeters`)}
        />
      </div>
      <p className="mt-1 text-sm text-primary-500">
        The form does not capture the lens spec — read it off the installed
        sensor.
      </p>

      <div className="mt-4">
        <PerspectiveTransformField
          label="Perspective transform"
          value={perspectiveTransform ?? null}
          onChange={setPerspectiveTransform}
        />
      </div>
    </div>
  );
};
